import { Config } from 'config/envConfig';
import React, { useState, useEffect } from 'react';
import { ConfirmModal, InputModal } from '@quyva/shared-ui';
import axios from 'axios';
import { generateUniqueNumber } from '@quyva/common';
import { ProcessType } from '@quyva/common';
import { TaskProgressType } from '@quyva/common';
import { getUrl } from './pdfDisplay/utils';
import { useMaintenanceWarning } from '../hooks/useMaintenanceWarning';

async function handleExcelFileDownload(email, folderName, templateName) {
    try {
        // Step 1: Fetch the list of Excel files from the backend
        const response = await fetch(
            getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/redownloadfile_excell?email=${email}&foldername=${folderName}&templatename=${templateName}`
            )
        );

        if (!response.ok) {
            throw new Error('Failed to fetch file list.');
        }

        const data = await response.json();

        if (!data.files || data.files.length === 0) {
            alert('No Excel files found for download.');
            return;
        }

        // Step 2: Download each file sequentially
        for (const file of data.files) {
            await downloadFile(email, folderName, templateName, file);
        }
    } catch (error) {
        console.error('Error downloading Excel files:', error);
        alert('Error downloading files. Please try again.');
    }
}

async function downloadFile(email, folderName, templateName, fileName) {
    try {
        // Fetch the Excel file from the backend
        const response = await fetch(
            getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/download_excel?email=${email}&foldername=${folderName}&templatename=${templateName}&filename=${fileName}`
            )
        );

        if (!response.ok) {
            throw new Error(`Failed to download ${fileName}`);
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    } catch (error) {
        console.error(`Error downloading ${fileName}:`, error);
        alert(`Error downloading ${fileName}.`);
    }
}

async function handleJsonFileDownload(email, folderName, templateName) {
    const uniqueId = generateUniqueNumber();

    const response = await fetch(
        getUrl(
            `http://${Config.FAST_API_HOST}:${
                Config.FAST_API_PORT
            }/redownloadfile_json?email=${email}&foldername=${folderName}&uid=${toString(
                uniqueId
            )}&templatename=${templateName}`
        )
    );

    const disposition = response.headers.get('Content-Disposition');

    if (disposition && disposition.includes('attachment')) {
        const fileNameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = fileNameRegex.exec(disposition);
        let filename = matches != null && matches[1] ? matches[1] : 'file.json';
        filename = filename.replace(/['"]/g, ''); // Remove any surrounding quotes

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    } else {
        const data = await response.json();
        alert(data.message);
    }
}

const updateActiveTemplate = async (user, activeTemplate) => {
    try {
        await axios.post(
            getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/updatetemplate`
            ),
            {
                id: user.email,
                csvname: activeTemplate.filename,
            }
        );
    } catch (error) {
        console.error('Error:', error);
    }
};

const Extraction = (props) => {
    const user = JSON.parse(localStorage.getItem('current'));
    const { setExtractMode, activeTemplate } = props;
    const [showModal, setShowModal] = useState(true);
    const [showInputModal, setShowInputModal] = useState(false);
    const [parameters, setParameters] = useState([]);
    const { warnIfNeeded, WarningModal } = useMaintenanceWarning();
    const [inputs, setInputs] = useState({});

    const fetchParameters = async () => {
        try {
            const response = await axios.post(
                getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/get-parameters`
                ),
                {
                    email: user.email,
                    template_name: activeTemplate.filename,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${user.token}`,
                    },
                }
            );

            const data = response.data;
            const params = data.parameters || [];
            console.log('Fetched Parameters:', params); // Log the parameters

            setParameters(params);

            const initialInputs = params.reduce((acc, param) => {
                acc[param.name] = '';
                return acc;
            }, {});
            setInputs(initialInputs);

            return params;
        } catch (error) {
            console.error(
                'Error fetching parameters:',
                error.response?.data?.error || error.message
            );
            return [];
        }
    };
    const extractInformation = async (inputValues = {}) => {
        warnIfNeeded('data extraction', async () => {
        setExtractMode(false);

        await updateActiveTemplate(user, activeTemplate);
        const token = user.token;

        const status = {
            message: `Starting extraction process on ${props.repo.name}`,
            percentageCompletion: 0,
            process: ProcessType.EXTRACTION,
            progress: TaskProgressType.STARTED,
            repoId: props.repo.repoid,
            repoName: props.repo.name,
            statusCode: 200,
            timeStamp: new Date(),
            userMail: user.email,
        };
        props.socket.emit('statusUpdate', status);
        const bodyData = new URLSearchParams();
        bodyData.append('email', props.repo.email);
        bodyData.append('user_email', user.email);
        bodyData.append('repo_name', props.repo.name);
        bodyData.append('template_name', props.activeTemplate.filename);
        bodyData.append('repoid', props.repo.repoid);
        bodyData.append('db_extraction', '0');
        bodyData.append('parameters', JSON.stringify(inputValues));
        bodyData.append(
            'is_type_temp',
            props.activeTemplate.is_categorization ? 'true' : 'false'
        );
        await fetch(
            getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/extract-information`
            ),
            {
                method: 'POST',
                headers: {
                    'Content-Type':
                        'application/x-www-form-urlencoded;charset=UTF-8',
                    Authorization: `Bearer ${token}`,
                },
                body: bodyData.toString(),
            }
        )
            .then(async (response) => {
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || errorData.detail);
                }
                return response.json(); // Parse JSON response
            })
            .then(async (data) => {
                if (data.detail) {
                    throw new Error(data.detail);
                }

                console.log('Received response:', data);

                const { files, file_names, template_list } = data;
                if (!files || files.length === 0) {
                    throw new Error('No files received for download');
                }

                // Download and handle all extracted files
                for (let i = 0; i < template_list.length; i++) {
                    const fileUrl = getUrl(files[i]);
                    const templateName = template_list[i]; // Use template name

                    // await fetch(fileUrl)
                    //     .then((fileResponse) => fileResponse.blob())
                    //     .then((blob) => {
                    //         const url = window.URL.createObjectURL(blob);
                    //         const a = document.createElement('a');
                    //         a.href = url;
                    //         a.download = file_names[i]; // Original file name
                    //         document.body.appendChild(a);
                    //         a.click();
                    //         document.body.removeChild(a);
                    //     });

                    // Handle JSON file download
                    // await handleJsonFileDownload(
                    //     props.repo.email,
                    //     props.repo.name,
                    //     templateName
                    // );

                    // Handle Excel file download
                    await handleExcelFileDownload(
                        props.repo.email,
                        props.repo.name,
                        templateName
                    );
                }
            })
            .then(async () => {
                await props.fetchDownloadList(props.repo.email);
            })
            .catch(async (error) => {
                await props.fetchDownloadList(props.repo.email);
                const errorMessage =
                    error.message || 'Error occurred while running extraction';
                alert(`Error: ${errorMessage}`);
            });
        }); // end warnIfNeeded callback
    };

    const handleConfirm = async () => {
        setShowModal(false);
        if (activeTemplate.filename.startsWith('default')) {
            await extractInformation();
        } else {
            const params = await fetchParameters();
            if (params.length > 0) {
                setShowInputModal(true);
            } else {
                await extractInformation();
            }
        }
    };

    const handleInputModalSubmit = async (inputValues) => {
        setShowInputModal(false);
        await extractInformation(inputValues);
    };

    return (
        <div>
            {showModal && (
                <ConfirmModal
                    modalText={`Extract '${props.repo.name}' repository's information?`}
                    onConfirm={handleConfirm}
                    onCancel={() => setExtractMode(false)}
                />
            )}
            {showInputModal && (
                <InputModal
                    parameters={parameters}
                    onSubmit={handleInputModalSubmit}
                    onCancel={() => setExtractMode(false)}
                />
            )}
        {WarningModal}
        </div>
    );
};

export default Extraction;
